export type SubPayOption = {
  id: string;
  title: string;
  description: string;
  price: number;
  badge?: string;
  recommended?: boolean;
  enabled?: boolean;
  action?: {
    type:
      | 'visibility_boost'
      | 'hochschieben_once'
      | 'highlight_7d'
      | 'hochschieben_daily_7d'
      | 'top_anzeige_7d'
      | 'galerie_startseite_10d'
      | 'custom';
    label?: string;
    details?: string;
    duration_days?: number;
  };
};

export type SubPayOptionsGroup = {
  id: string;
  currency: string;
  active?: boolean;
  options: SubPayOption[];
};

export type SubPayOptionsDocument = {
  version?: number;
  // v1 schema
  currency?: string;
  options?: SubPayOption[];
  // v2 schema
  groups?: SubPayOptionsGroup[];
};

export type ResolvedSubPayOptions = {
  currency: string;
  options: SubPayOption[];
  groupId?: string;
};

export class SubPayOptionsService {
  private static apiBase(): string {
    const envBase = (process.env.REACT_APP_SUB_PAY_OPTIONS_API_BASE as string | undefined) || '';
    const trimmed = String(envBase || '').trim();
    if (trimmed) return trimmed.replace(/\/$/, '');

    // Dev helper: when React runs on a different port than the PHP server (commonly 8000)
    // we must call the PHP origin explicitly.
    try {
      const { protocol, hostname, port } = window.location;
      if (port && port !== '8000') {
        return `${protocol}//${hostname}:8000`;
      }
    } catch {
      // ignore
    }
    return '';
  }

  private static url(path: string): string {
    const base = SubPayOptionsService.apiBase();
    return base ? `${base}${path}` : path;
  }

  static async getOptions(): Promise<SubPayOptionsDocument> {
    const res = await fetch(SubPayOptionsService.url('/api/get-sub-pay-options.php'), { cache: 'no-store' });
    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }
    if (!res.ok) {
      throw new Error(`${res.status} ${res.statusText} - ${raw || 'No response body'}`);
    }
    return (json || {}) as SubPayOptionsDocument;
  }

  static resolveActive(doc: SubPayOptionsDocument): ResolvedSubPayOptions {
    const groups = Array.isArray(doc.groups) ? doc.groups : null;
    if (groups && groups.length > 0) {
      const active = groups.find((g) => g && g.active) || groups[0];
      const currency = (active?.currency || 'EUR').trim() || 'EUR';
      const options = Array.isArray(active?.options) ? active.options : [];
      return { currency, options, groupId: active?.id };
    }

    // v1 fallback
    return {
      currency: (doc.currency || 'EUR').trim() || 'EUR',
      options: Array.isArray(doc.options) ? doc.options : [],
    };
  }

  static async updateOptions(input: { token: string; currency: string; options: SubPayOption[] }): Promise<SubPayOptionsDocument> {
    const res = await fetch(SubPayOptionsService.url('/api/update-sub-pay-options.php'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': input.token,
        'Authorization': `Bearer ${input.token}`,
      },
      body: JSON.stringify({ token: input.token, currency: input.currency, options: input.options }),
    });

    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const detail = json?.error || raw || 'No response body';
      throw new Error(`${res.status} ${res.statusText} - ${detail}`);
    }

    // New response shape: { ok: true, doc: {...} }
    if (json?.doc && typeof json.doc === 'object') {
      return json.doc as SubPayOptionsDocument;
    }
    // Backward compatibility (older endpoint versions)
    return { currency: json.currency, options: json.options } as SubPayOptionsDocument;
  }

  static async updateGroups(input: { token: string; groups: SubPayOptionsGroup[] }): Promise<SubPayOptionsDocument> {
    const res = await fetch(SubPayOptionsService.url('/api/update-sub-pay-options.php'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': input.token,
        'Authorization': `Bearer ${input.token}`,
      },
      body: JSON.stringify({ token: input.token, groups: input.groups }),
    });

    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const detail = json?.error || raw || 'No response body';
      throw new Error(`${res.status} ${res.statusText} - ${detail}`);
    }

    return (json?.doc || json || {}) as SubPayOptionsDocument;
  }
}
